import { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { useInfiniteDataSourcesQuery, useBulkReorderDataSourcesMutation } from '@/hooks/useDataSources'
import { useFoldersQuery, useReorderFoldersMutation, useMoveDatasourceToFolderMutation } from '@/hooks/useDatasourceFolders'
import { useConnectionStore } from '@/stores/connectionStore'
import { useSearchStore } from '@/stores/searchStore'
import { useDatasourceFolderStore } from '@/stores/datasourceFolderStore'
import { useAuthStore } from '@/stores/authStore'
import { useAgents } from '@/hooks/useAgents'
import { DatasourceList } from '@/components/connections/DatasourceList'
import { ConnectionModal } from '@/components/connections/ConnectionModal'
import { CreateFolderModal } from '@/components/connections/CreateFolderModal'
import { ExportConnectionsModal } from '@/components/connections/ExportConnectionsModal'
import { ImportConnectionsModal } from '@/components/connections/ImportConnectionsModal'
import { PlusIcon, FolderPlusIcon, MagnifyingGlassIcon, XMarkIcon, ArrowDownTrayIcon, ArrowUpTrayIcon, ChevronDoubleDownIcon, ChevronDoubleUpIcon } from '@heroicons/react/24/outline'
import type { DataSource } from '@/services/api'
import { notify } from '@/utils/notify'
import Fuse from 'fuse.js'

export function ConnectionsPage() {
  // Fetch more items for row layout
  const {
    data,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch
  } = useInfiniteDataSourcesQuery(100)
  const { data: folders = [], isLoading: isFoldersLoading } = useFoldersQuery()
  const { activeConnection } = useConnectionStore()
  const { connectionsSearch, setConnectionsSearch, clearConnectionsSearch } = useSearchStore()
  const { expandAll, collapseAll, hasExpandedFolders, cleanupDeletedFolders } = useDatasourceFolderStore()
  const [showModal, setShowModal] = useState(false)
  const [editingConnection, setEditingConnection] = useState<DataSource | null>(null)
  const [showExportModal, setShowExportModal] = useState(false)
  const [showImportModal, setShowImportModal] = useState(false)
  const [showCreateFolderModal, setShowCreateFolderModal] = useState(false)
  const [createFolderParentId, setCreateFolderParentId] = useState<string | null>(null)
  const bulkReorderMutation = useBulkReorderDataSourcesMutation()
  const reorderFoldersMutation = useReorderFoldersMutation()
  const moveDatasourceToFolderMutation = useMoveDatasourceToFolderMutation()

  // Fetch agents for import modal
  const { agents, fetchAgents } = useAgents()
  const hasLoadedAgentsRef = useRef(false)

  // Fuzzy search configuration for datasources
  // 0.0 = exact match, 1.0 = matches anything
  const datasourceFuseOptions = useMemo(() => ({
    keys: ['displayName'],
    threshold: 0.4,
    includeScore: true,
    minMatchCharLength: 1,
    ignoreLocation: true,
  }), [])

  // Fuzzy search configuration for folders
  const folderFuseOptions = useMemo(() => ({
    keys: ['name'],
    threshold: 0.4,
    includeScore: true,
    minMatchCharLength: 1,
    ignoreLocation: true,
  }), [])

  // Real-time search functionality with persistent state
  const handleSearchChange = useCallback((query: string) => {
    setConnectionsSearch(query)
  }, [setConnectionsSearch])

  const handleClearSearch = useCallback(() => {
    clearConnectionsSearch()
  }, [clearConnectionsSearch])

  // Get total connections count and all connections (without filtering - filtering happens in DatasourceList)
  const { totalConnections, connections } = useMemo(() => {
    const allConnections = data?.pages.flatMap(page => page.data) ?? []
    const totalCount = allConnections.length

    // Sort by order field
    const sortedConnections = [...allConnections].sort((a, b) => {
      if (a.order !== undefined && b.order !== undefined) {
        return a.order - b.order
      }
      return 0
    })

    return { totalConnections: totalCount, connections: sortedConnections }
  }, [data])

  // Create Fuse instances only when data changes (not on every search)
  const datasourceFuse = useMemo(() => {
    return new Fuse(connections, datasourceFuseOptions)
  }, [connections, datasourceFuseOptions])

  const folderFuse = useMemo(() => {
    return new Fuse(folders, folderFuseOptions)
  }, [folders, folderFuseOptions])

  // Search results - find matching datasource and folder IDs
  const { matchingDatasourceIds, matchingFolderIds } = useMemo(() => {
    if (!connectionsSearch.trim()) {
      return { matchingDatasourceIds: undefined, matchingFolderIds: undefined }
    }

    // Search datasources using pre-created Fuse instance
    const datasourceResults = datasourceFuse.search(connectionsSearch)
    const matchingDs = new Set<string>(
      datasourceResults.map(r => r.item.id).filter((id): id is string => !!id)
    )

    // Search folders using pre-created Fuse instance
    const folderResults = folderFuse.search(connectionsSearch)
    const matchingF = new Set<string>(
      folderResults.map(r => r.item.id).filter((id): id is string => !!id)
    )

    return { matchingDatasourceIds: matchingDs, matchingFolderIds: matchingF }
  }, [connectionsSearch, datasourceFuse, folderFuse])

  // Determine if search should be shown (only show if 2+ datasources)
  const shouldShowSearch = totalConnections >= 2

  // Clear search when search should be hidden
  useEffect(() => {
    if (!shouldShowSearch && connectionsSearch) {
      clearConnectionsSearch()
    }
  }, [shouldShowSearch, connectionsSearch, clearConnectionsSearch])

  // Stable callback functions to prevent re-renders
  const handleReload = useCallback(() => {
    window.location.reload()
  }, [])

  const handleCreateConnection = useCallback(() => {
    setEditingConnection(null)
    setShowModal(true)
  }, [])

  const handleOpenExportModal = useCallback(() => {
    setShowExportModal(true)
  }, [])

  const handleCloseExportModal = useCallback(() => {
    setShowExportModal(false)
  }, [])

  const handleOpenImportModal = useCallback(() => {
    // Load agents when opening import modal
    if (!hasLoadedAgentsRef.current) {
      hasLoadedAgentsRef.current = true
      // Load up to 100 agents
      void fetchAgents(1, 100, {})
    }
    setShowImportModal(true)
  }, [fetchAgents])

  const handleCloseImportModal = useCallback(() => {
    setShowImportModal(false)
  }, [])

  const handleOpenCreateFolderModal = useCallback(() => {
    setCreateFolderParentId(null)
    setShowCreateFolderModal(true)
  }, [])

  const handleCloseCreateFolderModal = useCallback(() => {
    setShowCreateFolderModal(false)
    setCreateFolderParentId(null)
  }, [])

  const handleCreateSubfolder = useCallback((parentFolderId: string) => {
    setCreateFolderParentId(parentFolderId)
    setShowCreateFolderModal(true)
  }, [])

  // Get all folder IDs for expand all functionality
  const allFolderIds = useMemo(() => {
    return folders.map(f => f.id).filter((id): id is string => !!id)
  }, [folders])

  // Reconcile persisted folder expansion state with current backend data.
  // Removes any expanded folder IDs that no longer exist on the server
  // (e.g., folders deleted while the user was logged out).
  const hasReconciledRef = useRef(false)
  useEffect(() => {
    if (hasReconciledRef.current || isFoldersLoading) return
    hasReconciledRef.current = true
    cleanupDeletedFolders(allFolderIds)
  }, [allFolderIds, isFoldersLoading, cleanupDeletedFolders])

  const handleExpandAll = useCallback(() => {
    expandAll(allFolderIds)
  }, [expandAll, allFolderIds])

  const handleCollapseAll = useCallback(() => {
    collapseAll()
  }, [collapseAll])

  // Check if there are any expanded folders
  const anyFoldersExpanded = hasExpandedFolders()

  const handleEditDatasource = useCallback((datasource: DataSource) => {
    setEditingConnection(datasource)
    setShowModal(true)
  }, [])

  // Drag-and-drop reorder handlers
  const handleReorderDatasources = useCallback(async (orderedIds: string[]) => {
    try {
      await bulkReorderMutation.mutateAsync(orderedIds)
    } catch {
      notify.error('Failed to reorder connections')
    }
  }, [bulkReorderMutation])

  const handleReorderFolders = useCallback(async (orderedIds: string[]) => {
    try {
      await reorderFoldersMutation.mutateAsync(orderedIds)
    } catch {
      notify.error('Failed to reorder folders')
    }
  }, [reorderFoldersMutation])

  const handleMoveDatasourceToFolder = useCallback(async (datasourceId: string, folderId: string | null) => {
    try {
      await moveDatasourceToFolderMutation.mutateAsync({ datasourceId, folderId })
      notify.success(folderId ? 'Connection moved to folder' : 'Connection moved to root')
    } catch {
      notify.error('Failed to move connection')
    }
  }, [moveDatasourceToFolderMutation])

  // Track if initial fetch has been done to prevent duplicates
  const hasFetchedRef = useRef(false)

  // Fetch fresh data on mount, avoiding duplicates
  // Run on mount and when refetch changes
  useEffect(() => {
    // Prevent duplicate fetches (React StrictMode, re-renders, etc.)
    if (hasFetchedRef.current) return
    hasFetchedRef.current = true

    void refetch()
  }, [refetch])

  // Auto-fetch second page on initial load to ensure scroll visibility
  const hasAutoFetchedSecondPage = useRef(false)

  useEffect(() => {
    // Only auto-fetch if:
    // 1. We haven't already done this
    // 2. We have loaded at least one page
    // 3. There's a next page available
    // 4. We're not currently fetching
    // 5. We have exactly one page loaded
    if (
      !hasAutoFetchedSecondPage.current &&
      data?.pages.length === 1 &&
      hasNextPage &&
      !isFetchingNextPage &&
      !isLoading
    ) {
      hasAutoFetchedSecondPage.current = true
      console.log('Auto-fetching second page of datasources...')
      void fetchNextPage()
    }
  }, [data?.pages.length, hasNextPage, isFetchingNextPage, isLoading, fetchNextPage])

  // Auto-refresh datasources every 5 seconds to keep data fresh (pause when modal is open)
  useEffect(() => {
    if (showModal) {
      // Don't auto-refresh when editing
      return
    }

    let intervalId: NodeJS.Timeout | undefined

    // Start periodic refresh after initial delay to avoid conflicts with mount fetch
    const timeoutId = setTimeout(() => {
      // Refresh every 5 seconds, but skip if auth token is missing (avoids 401 spam during token refresh)
      intervalId = setInterval(() => {
        const token = useAuthStore.getState().token
        if (token) {
          void refetch()
        }
      }, 5000)
      // Wait 5 seconds before starting periodic refresh
    }, 5000)

    return () => {
      clearTimeout(timeoutId)
      if (intervalId) clearInterval(intervalId)
    }
  }, [refetch, showModal])

  // Set up infinite scroll using the main container
  useEffect(() => {
    let scrollTimeout: NodeJS.Timeout

    const handleScroll = (event: Event) => {
      // Debounce scroll events to prevent excessive calls
      clearTimeout(scrollTimeout)
      // 100ms debounce
      scrollTimeout = setTimeout(() => {
        const {target} = event
        if (!target || !(target instanceof HTMLElement)) return

        const { scrollTop, scrollHeight, clientHeight } = target
        const isNearBottom = scrollTop + clientHeight >= scrollHeight - 300

        if (isNearBottom && hasNextPage && !isFetchingNextPage) {
          console.log('Loading more connections...')
          void fetchNextPage()
        }
      }, 100)
    }

    // Find the main scrollable container
    const mainElement = document.querySelector('main')
    if (mainElement) {
      mainElement.addEventListener('scroll', handleScroll, { passive: true })
      return () => {
        mainElement.removeEventListener('scroll', handleScroll)
        clearTimeout(scrollTimeout)
      }
    }
    
    return () => {
      clearTimeout(scrollTimeout)
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  const handleCloseModal = useCallback(() => {
    setShowModal(false)
    setEditingConnection(null)
  }, [])

  if (isLoading || isFoldersLoading) {
    return (
      <div className="p-6">
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <div className="text-red-500 mb-4">Failed to load connections</div>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            {error.message || 'An error occurred while loading connections'}
          </p>
          <button
            type="button"
            onClick={handleReload}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Database Connections</h1>
            <div className="flex items-center space-x-2">
              <p className="text-gray-600 dark:text-gray-400">
                Manage your database connections
              </p>
              {activeConnection && (
                <div className="flex items-center space-x-2 px-3 py-1 bg-green-100 dark:bg-green-900 rounded-full">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <span className="text-sm text-green-700 dark:text-green-300">
                    Connected to {activeConnection.name}
                  </span>
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center space-x-2">
            {connections.length > 0 && (
              <>
                <button type="button"
                  onClick={handleOpenExportModal}
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg shadow-sm hover:shadow-md focus:outline-none focus:ring-0 transition-all duration-150"
                  title="Export connections"
                >
                  <ArrowDownTrayIcon className="w-4 h-4 mr-2" />
                  <span>Export</span>
                </button>
              </>
            )}
            <button type="button"
              onClick={handleOpenImportModal}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg shadow-sm hover:shadow-md focus:outline-none focus:ring-0 transition-all duration-150"
              title="Import connections"
            >
              <ArrowUpTrayIcon className="w-4 h-4 mr-2" />
              <span>Import</span>
            </button>
            <button type="button"
              onClick={handleOpenCreateFolderModal}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg shadow-sm hover:shadow-md focus:outline-none focus:ring-0 transition-all duration-150"
              title="Create folder"
            >
              <FolderPlusIcon className="w-4 h-4 mr-2" />
              <span>New Folder</span>
            </button>
            <button type="button"
              onClick={handleCreateConnection}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:bg-blue-700 rounded-lg shadow-sm hover:shadow-md focus:outline-none focus:ring-0 transition-all duration-150"
            >
              <PlusIcon className="w-4 h-4 mr-2" />
              <span>New Connection</span>
            </button>
          </div>
        </div>
        
        {/* Search Bar and Expand/Collapse Buttons */}
        {shouldShowSearch && (
          <>
            <div className="mt-4 flex items-center gap-2">
              <div className="relative max-w-md flex-1">
                <MagnifyingGlassIcon className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={connectionsSearch}
                  placeholder="Search connections by name..."
                  className="w-full pl-10 pr-10 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:border-gray-500 dark:focus:border-gray-400 focus:ring-0 focus:outline-none transition-colors"
                  onChange={(e) => { handleSearchChange(e.target.value) }}
                />
                {connectionsSearch && (
                  <button
                    type="button"
                    onClick={handleClearSearch}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                    title="Clear search"
                    aria-label="Clear search"
                  >
                    <XMarkIcon className="w-4 h-4" aria-hidden="true" />
                  </button>
                )}
              </div>
              {folders.length > 0 && (
                <>
                  <button
                    type="button"
                    onClick={handleExpandAll}
                    className="inline-flex items-center px-2.5 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
                    title="Expand all folders"
                    aria-label="Expand all folders"
                  >
                    <ChevronDoubleDownIcon className="w-4 h-4" aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    onClick={handleCollapseAll}
                    disabled={!anyFoldersExpanded}
                    className="inline-flex items-center px-2.5 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors"
                    title="Collapse all folders"
                    aria-label="Collapse all folders"
                  >
                    <ChevronDoubleUpIcon className="w-4 h-4" aria-hidden="true" />
                  </button>
                </>
              )}
            </div>
            
            {/* Search Results Summary */}
            {connectionsSearch && (
              <div className="mt-3 text-sm text-gray-600 dark:text-gray-400">
                {(() => {
                  const dsCount = matchingDatasourceIds?.size ?? 0
                  const folderCount = matchingFolderIds?.size ?? 0
                  const totalMatches = dsCount + folderCount

                  if (totalMatches === 0) {
                    return <span>No results found for "{connectionsSearch}"</span>
                  }

                  const parts: string[] = []
                  if (dsCount > 0) {
                    parts.push(`${dsCount} connection${dsCount !== 1 ? 's' : ''}`)
                  }
                  if (folderCount > 0) {
                    parts.push(`${folderCount} folder${folderCount !== 1 ? 's' : ''}`)
                  }

                  return (
                    <span>
                      Found {parts.join(' and ')} matching "{connectionsSearch}"
                    </span>
                  )
                })()}
              </div>
            )}
          </>
        )}
      </div>

      {connections.length === 0 ? (
        <div className="text-center py-12">
          <div className="w-16 h-16 mx-auto bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mb-4">
            {connectionsSearch ? (
              <MagnifyingGlassIcon className="w-8 h-8 text-gray-400" />
            ) : (
              <PlusIcon className="w-8 h-8 text-gray-400" />
            )}
          </div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            {connectionsSearch ? 'No matching connections' : 'No connections yet'}
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            {connectionsSearch 
              ? `No connections match "${connectionsSearch}". Try adjusting your search terms.`
              : 'Get started by creating your first database connection.'
            }
          </p>
          {connectionsSearch ? (
            <button type="button"
              onClick={handleClearSearch}
              className="inline-flex items-center px-5 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg shadow-sm hover:shadow-md focus:outline-none focus:ring-0 transition-all duration-150"
            >
              <XMarkIcon className="w-4 h-4 mr-2" />
              <span>Clear Search</span>
            </button>
          ) : (
            <button type="button"
              onClick={handleCreateConnection}
              className="inline-flex items-center px-5 py-2.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:bg-blue-700 rounded-lg shadow-sm hover:shadow-md focus:outline-none focus:ring-0 transition-all duration-150"
            >
              <PlusIcon className="w-4 h-4 mr-2" />
              <span>Create Connection</span>
            </button>
          )}
        </div>
      ) : (
        <>
          <DatasourceList
            datasources={connections}
            folders={folders}
            onEditDatasource={handleEditDatasource}
            onReorderDatasources={(orderedIds) => { void handleReorderDatasources(orderedIds) }}
            onReorderFolders={(orderedIds) => { void handleReorderFolders(orderedIds) }}
            onMoveDatasourceToFolder={(datasourceId, folderId) => { void handleMoveDatasourceToFolder(datasourceId, folderId) }}
            onCreateSubfolder={handleCreateSubfolder}
            searchQuery={connectionsSearch}
            matchingFolderIds={matchingFolderIds}
            matchingDatasourceIds={matchingDatasourceIds}
          />

          {/* Loading indicator for infinite scroll */}
          {isFetchingNextPage && (
            <div className="flex justify-center items-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
              <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">
                Loading more connections...
              </span>
            </div>
          )}

          {/* End of results indicator */}
          {!hasNextPage && !isFetchingNextPage && connections.length > 12 && (
            <div className="text-center py-8">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                You've reached the end of your connections ({connections.length} total)
              </p>
            </div>
          )}
        </>
      )}

      {showModal && (
        <ConnectionModal
          existingConnection={editingConnection ?? undefined}
          onClose={handleCloseModal}
        />
      )}

      {showExportModal && (
        <ExportConnectionsModal
          isOpen={showExportModal}
          onClose={handleCloseExportModal}
          connections={connections}
        />
      )}

      {showImportModal && (
        <ImportConnectionsModal
          isOpen={showImportModal}
          onClose={handleCloseImportModal}
          agents={agents}
        />
      )}

      <CreateFolderModal
        isOpen={showCreateFolderModal}
        onClose={handleCloseCreateFolderModal}
        folders={folders}
        defaultParentFolderId={createFolderParentId}
      />
    </div>
  )
}
